import React, { useEffect, useState } from 'react';
import ClosestDeals from '../components/ClosestDeals';
import Disclaimer from '../components/Disclaimer';
import PerLife from '../components/PerLife';
import ShareDialog from '../components/ShareDialog';
import { tierContextMap } from '../data/tierContextMap';
import { formatUSD, formatUSDCompact } from '../lib/format';
import { TIERS_LOW_TO_HIGH, tierHeadline } from '../lib/tierPercentile';
import { factorTitle } from '../lib/strength';
import { FACTOR_KEYS, incompleteBlocker } from '../data/defaultInputs';
import { describeError } from '../lib/describeError';
import { generatePdf } from '../api/pdf';
import { listDiseaseCategories } from '../api/catalogs';
import StepHero from './StepHero';
import WizardBlockerCard from '../components/WizardBlockerCard';

const TIER_RANGES = {
  Entry: '< 10',
  Competitive: '10–20',
  Standard: '20–30',
  'High-Value': '30–40',
  Premium: '40–100',
  'Ultra-Premium': '≥ 100',
};

const TIER_SHORT = {
  Entry: 'Entry',
  Competitive: 'Comp.',
  Standard: 'Standard',
  'High-Value': 'High',
  Premium: 'Premium',
  'Ultra-Premium': 'Ultra',
};

export default function StepEstimate({
  result,
  inputs,
  name,
  publicId,
  ready = false,
  calcLoading = false,
  calcError = null,
  onRetry,
  onGoToStep,
  onShowMethodology,
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [diseaseBenchmark, setDiseaseBenchmark] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!inputs?.diseaseCategory || inputs.diseaseCategory === 'none') {
      setDiseaseBenchmark(null);
      return;
    }
    let cancelled = false;
    listDiseaseCategories()
      .then((r) => {
        if (cancelled) return;
        const match = (r.categories || []).find((c) => c.value === inputs.diseaseCategory);
        if (match?.per_life_range) {
          setDiseaseBenchmark({
            low: match.per_life_range.low,
            high: match.per_life_range.high,
            note: match.per_life_range.note,
          });
        }
      })
      .catch(() => { /* silently degrade — PerLife renders without benchmark */ });
    return () => { cancelled = true; };
  }, [inputs?.diseaseCategory]);

  if (!result) {
    return (
      <EstimatePending
        inputs={inputs}
        calcError={calcError}
        calcLoading={calcLoading}
        ready={ready}
        onGoToStep={onGoToStep}
        onRetry={onRetry}
      />
    );
  }

  async function handleDownload() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await generatePdf({ name, inputs });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'valuation').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err?.code || 'pdf_failed');
    } finally {
      setDownloading(false);
    }
  }

  const ctx = tierContextMap[result.valueCategory];

  return (
    <section data-testid="step-estimate" style={containerStyle}>
      <EstimateHero
        name={name}
        result={result}
        inputs={inputs}
        ctx={ctx}
        onDownload={handleDownload}
        downloading={downloading}
        downloadError={downloadError}
        onShare={publicId ? () => setShareOpen(true) : null}
      />

      {shareOpen && (
        <ShareDialog publicId={publicId} valuationName={name} onClose={() => setShareOpen(false)} />
      )}

      <TierLadder activeTier={result.valueCategory} />

      <RevenueCards
        pharmaAnnual={result.pharmaAnnual}
        aiMonthly={result.aiMonthly}
        consortiumPerInst={result.consortiumPerInst}
        pricePerPatient={result.pricePerPatient}
        consentTier={result.consentTier}
        multiYear={inputs.multiYear}
        exclusivity={inputs.exclusivity}
      />

      <PerLife pharmaAnnual={result.pharmaAnnual} samples={inputs.samples} benchmark={diseaseBenchmark} />

      <ThreeYearLicenseCard
        threeYearValue={result.threeYearValue}
        pharmaAnnual={result.pharmaAnnual}
      />

      <ValueDriversCard inputs={inputs} result={result} ctx={ctx} />

      {onShowMethodology && (
        <div style={methodologyLinkStyle}>
          <button type="button" onClick={onShowMethodology} style={methodologyBtnStyle}>
            How is this calculated? →
          </button>
        </div>
      )}

      <Disclaimer />
    </section>
  );
}

function EstimatePending({ inputs, calcError, calcLoading, ready, onGoToStep, onRetry }) {
  const blocker = incompleteBlocker(inputs);

  return (
    <section data-testid="step-estimate" style={pendingSectionStyle}>
      <StepHero
        sectionLabel="Final estimate"
        title={<>Your estimate <em style={pendingEmStyle}>isn&apos;t ready yet.</em></>}
        lede="Finish the highlighted wizard step, then return here — your tier and revenue models will appear once the inputs are complete."
      />

      <WizardBlockerCard blocker={blocker} onGoToStep={onGoToStep} testId="estimate-go-to-step" />

      {!blocker && calcError && (
        <div style={errorCardStyle} role="alert">
          <AlertCircleIcon tone="error" />
          <span style={blockerBodyStyle}>
            <span style={blockerTitleStyle}>Couldn&apos;t calculate your estimate</span>
            <span style={blockerDetailStyle}>
              {describeError('calc', calcError) || 'Something went wrong talking to the server.'}
            </span>
          </span>
          {onRetry && (
            <button type="button" onClick={onRetry} style={retryInlineStyle}>
              Retry
            </button>
          )}
        </div>
      )}

      {!blocker && !calcError && (calcLoading || ready) && (
        <div style={loadingCardStyle} aria-live="polite">
          <span style={loadingDotStyle} aria-hidden="true" />
          <span style={blockerDetailStyle}>Calculating your estimate…</span>
        </div>
      )}
    </section>
  );
}

function AlertCircleIcon({ tone }) {
  const color = tone === 'error' ? 'var(--error)' : 'var(--amber-strong)';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, color }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" fill="none" />
      <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────
function EstimateHero({ name, result, inputs, ctx, onDownload, downloading, downloadError, onShare }) {
  const headline = tierHeadline(result.valueCategory);
  return (
    <div style={heroStyle}>
      <div>
        <div style={eyebrowStyle}>Final estimate</div>
        <h1 style={titleStyle}>{name || 'Untitled valuation'}</h1>
        <p style={subStyle}>{ctx?.description}</p>
        <div style={actionsStyle}>
          <button type="button" onClick={onDownload} disabled={downloading} style={downloading ? actionDisabledStyle : accentActionStyle}>
            {downloading ? 'Generating PDF…' : 'Export PDF'}
            {!downloading && <ArrowIcon />}
          </button>
          {onShare && (
            <button type="button" onClick={onShare} style={secondaryActionStyle}>
              Share link
              <ArrowIcon />
            </button>
          )}
        </div>
        {downloadError && <div style={errorRowStyle}>Couldn't generate PDF: {downloadError}</div>}
      </div>

      <div style={tierSummaryStyle}>
        <div style={scoreLabelStyle}>Value tier</div>
        <div style={tierNameStyle}>{result.valueCategory}</div>
        <div style={scoreFormulaStyle}>
          {headline} · {result.consentTier} consent
        </div>
      </div>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────
function TierLadder({ activeTier }) {
  return (
    <div style={ladderContainerStyle}>
      <div style={ladderLabelStyle}>Tier ladder</div>
      <div style={ladderRowStyle}>
        {TIERS_LOW_TO_HIGH.map((tier) => {
          const isActive = tier === activeTier;
          return (
            <div key={tier} style={isActive ? ladderSegActiveStyle : ladderSegStyle}>
              <div style={ladderNameStyle}>
                {isActive && <span style={ladderDotStyle}>●</span>}
                {TIER_SHORT[tier]}
              </div>
              <div style={isActive ? ladderRangeActiveStyle : ladderRangeStyle}>{TIER_RANGES[tier]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function RevenueCards({ pharmaAnnual, aiMonthly, consortiumPerInst, pricePerPatient, multiYear, exclusivity }) {
  const aiAnnual = (aiMonthly || 0) * 12;
  return (
    <div style={revGridStyle}>
      <RevCard
        accent="var(--accent)"
        name="Pharma partnership"
        subtitle="Annual license"
        amount={formatUSDCompact(pharmaAnnual)}
        unit="/ yr"
        meta="Base license fee for a top-20 pharma or large biotech partnership."
        tags={[
          { label: `${formatUSD(pricePerPatient)} / patient` },
          { label: `${multiYear}-year term` },
          { label: exclusivity ? 'Exclusive' : 'Non-exclusive', tone: 'accent' },
        ]}
      />
      <RevCard
        accent="var(--navy)"
        name="AI training data"
        subtitle="Monthly subscription"
        amount={formatUSDCompact(aiMonthly)}
        unit="/ mo"
        meta={`Annual: ${formatUSDCompact(aiAnnual)}. Multi-year and exclusivity terms don't stack on the AI line.`}
        tags={[]}
      />
      <RevCard
        accent="var(--amber)"
        name="Research consortium"
        subtitle="Per institution · annual"
        amount={formatUSDCompact(consortiumPerInst)}
        unit="/ inst"
        meta="consortium = round(pharma_annual × 0.2). Typical consortium: 4–8 academic + 1 pharma anchor."
        tags={[]}
      />
    </div>
  );
}

function RevCard({ accent, name, subtitle, amount, unit, meta, tags }) {
  return (
    <div style={{ ...revCardStyle, borderTopColor: accent }}>
      <div style={revNameStyle}>{name}</div>
      <div style={revSubtitleStyle}>{subtitle}</div>
      <div style={revAmountStyle}>
        <span style={revCurrencyStyle}>$</span>
        {amount.replace(/^\$/, '')}
        <span style={revPeriodStyle}>{unit}</span>
      </div>
      <div style={revMetaStyle}>{meta}</div>
      {tags?.length > 0 && (
        <div style={revTagsStyle}>
          {tags.map((t, i) => (
            <span key={i} style={t.tone === 'accent' ? tagAccentStyle : tagStyle}>{t.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ThreeYearLicenseCard({ threeYearValue, pharmaAnnual }) {
  return (
    <div style={tcStyle}>
      <div>
        <div style={tcLabelStyle}>Three years of base license fees</div>
        <div style={tcSubStyle}>Estimated total for a standard 3-year pharma partnership term.</div>
        <div style={tcFormulaStyle}>
          {formatUSD(pharmaAnnual)} × 3 = {formatUSD(threeYearValue)}
        </div>
      </div>
      <div style={tcAmountStyle}>{formatUSDCompact(threeYearValue)}</div>
      <div style={tcFootStyle}>Illustrative estimate — actual deal terms vary by buyer, indication, and negotiation.</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ValueDriversCard({ inputs, result, ctx }) {
  return (
    <div style={insightsRowStyle}>
      <div style={insightCardStyle}>
        <div style={insightHeadStyle}>
          <BoltIcon />
          Value drivers
        </div>
        {FACTOR_KEYS.map((f) => {
          const score = inputs[f];
          const pct = (score / 5) * 100;
          const isPrimary = score >= 4;
          const isLift = score <= 3;
          return (
            <div key={f} style={driverRowStyle}>
              <div style={driverNameStyle}>{factorTitle(f)}</div>
              <span style={tagStyle}>{score} / 5</span>
              <div style={driverBarStyle}>
                <span style={{ ...driverBarFillStyle, width: `${pct}%`, background: isLift ? 'var(--amber)' : 'var(--accent)' }} />
              </div>
              <span style={isPrimary ? driverLabelPrimaryStyle : driverLabelStyle}>
                {isPrimary ? 'primary' : isLift ? 'lift this' : 'solid'}
              </span>
            </div>
          );
        })}
      </div>

      <ClosestDeals inputs={inputs} result={result} />
    </div>
  );
}

function BoltIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────
// styles
const containerStyle = { display: 'flex', flexDirection: 'column', gap: '28px', paddingBottom: '40px' };
const pendingSectionStyle = { display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '40px' };
const pendingEmStyle = { fontStyle: 'italic', color: 'var(--amber-strong)' };

const errorCardBase = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '16px 18px',
  borderRadius: '12px',
  textAlign: 'left',
  fontFamily: 'var(--font)',
};
const errorCardStyle = {
  ...errorCardBase,
  cursor: 'default',
  background: '#fef2f2',
  border: '1px solid rgba(239, 68, 68, 0.25)',
  boxShadow: 'var(--shadow-sm)',
};
const loadingCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 18px',
  borderRadius: '12px',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
};
const blockerBodyStyle = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
const blockerTitleStyle = { fontSize: '15px', fontWeight: 600, color: 'var(--ink)' };
const blockerDetailStyle = { fontSize: '13px', lineHeight: 1.5, color: 'var(--ink2)' };
const retryInlineStyle = {
  flexShrink: 0,
  height: '36px',
  padding: '0 14px',
  borderRadius: '999px',
  border: '1px solid var(--line)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'var(--font)',
};
const loadingDotStyle = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: 'var(--accent)',
  flexShrink: 0,
  animation: 'loopora-pulse 1.2s ease-in-out infinite',
};

const heroStyle = { display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '48px', alignItems: 'end' };
const eyebrowStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '14px', color: 'var(--amber)' };
const titleStyle = { fontFamily: 'var(--display)', fontSize: '44px', fontWeight: 500, letterSpacing: '-0.025em', margin: '8px 0 0', lineHeight: 1.05, color: 'var(--ink)' };
const subStyle = { fontSize: '15px', color: 'var(--ink2)', lineHeight: 1.55, margin: '14px 0 0', maxWidth: '480px' };
const actionsStyle = { marginTop: '22px', display: 'flex', gap: '8px' };
const errorRowStyle = { marginTop: '10px', fontSize: '12.5px', color: 'var(--warm)' };

const btnBase = { display: 'inline-flex', alignItems: 'center', gap: '8px', height: '40px', padding: '0 18px', borderRadius: '999px', fontSize: '13.5px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)' };
const accentActionStyle = { ...btnBase, background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' };
const secondaryActionStyle = { ...btnBase, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)' };
const actionDisabledStyle = { ...btnBase, background: 'var(--line)', border: '1px solid var(--line)', color: 'var(--muted)', cursor: 'not-allowed' };

const tierSummaryStyle = { textAlign: 'right' };
const scoreLabelStyle = { fontSize: '12px', color: 'var(--muted)', letterSpacing: '0.04em', textTransform: 'uppercase' };
const tierNameStyle = {
  fontFamily: 'var(--display)',
  fontStyle: 'italic',
  fontSize: '48px',
  fontWeight: 500,
  lineHeight: 1.05,
  color: 'var(--accent)',
  letterSpacing: '-0.03em',
  marginTop: '8px',
};
const scoreFormulaStyle = { fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--muted)', marginTop: '10px' };

const ladderContainerStyle = { marginTop: '8px' };
const ladderLabelStyle = { fontSize: '12.5px', color: 'var(--muted)' };
const ladderRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px', marginTop: '8px' };
const ladderSegBase = { padding: '14px 8px 12px', textAlign: 'center', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--line)', borderRadius: '4px', background: 'var(--paper)' };
const ladderSegStyle = ladderSegBase;
const ladderSegActiveStyle = { ...ladderSegBase, background: 'var(--ink)', color: 'var(--bg)', borderColor: 'var(--ink)' };
const ladderNameStyle = { fontSize: '12px', fontWeight: 500 };
const ladderDotStyle = { color: 'var(--accent)', marginRight: '4px' };
const ladderRangeStyle = { fontSize: '10.5px', color: 'var(--muted)', marginTop: '4px', fontVariantNumeric: 'tabular-nums' };
const ladderRangeActiveStyle = { ...ladderRangeStyle, color: 'rgba(251, 247, 238, 0.6)' };

const revGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '18px' };
const revCardStyle = { padding: '22px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '4px', borderTop: '3px solid var(--accent)' };
const revNameStyle = { fontSize: '15px', fontWeight: 600, color: 'var(--ink)' };
const revSubtitleStyle = { fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginTop: '4px' };
const revAmountStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '38px', fontWeight: 500, color: 'var(--ink)', marginTop: '18px', letterSpacing: '-0.02em' };
const revCurrencyStyle = { fontSize: '18px', color: 'var(--muted)', marginRight: '4px', fontStyle: 'normal' };
const revPeriodStyle = { fontSize: '14px', color: 'var(--muted)', marginLeft: '4px', fontStyle: 'normal' };
const revMetaStyle = { fontSize: '12.5px', color: 'var(--ink2)', marginTop: '14px', lineHeight: 1.45 };
const revTagsStyle = { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' };
// Tag chips default to amber per Sarah's feedback ("chips should use amber
// from stylesheet, e.g. '5-year term'"). Accent variant stays teal — used
// when a tag is conveying a primary highlight (e.g., "Non-exclusive" on
// the Pharma card).
const tagStyle = { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', background: 'var(--amber-bg)', color: 'var(--amber-strong)', fontSize: '11px', fontWeight: 500 };
const tagAccentStyle = { ...tagStyle, background: 'var(--accent-bg)', color: 'var(--accent-strong)' };

const tcStyle = { padding: '26px 28px', background: 'linear-gradient(180deg, #2C261C 0%, #1F1A12 100%)', color: 'var(--bg)', borderRadius: '6px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '32px' };
const tcLabelStyle = { fontSize: '11px', letterSpacing: '0.08em', color: 'var(--accent)', textTransform: 'uppercase', fontWeight: 500 };
const tcSubStyle = { fontSize: '13.5px', color: 'rgba(251, 247, 238, 0.65)', marginTop: '6px' };
const tcFormulaStyle = { fontFamily: 'var(--mono)', fontSize: '11px', color: 'rgba(251, 247, 238, 0.45)', marginTop: '10px' };
const tcAmountStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '56px', fontWeight: 500, color: 'var(--bg)', letterSpacing: '-0.03em', lineHeight: 1 };
const tcFootStyle = { fontSize: '11.5px', color: 'rgba(251, 247, 238, 0.45)', marginTop: '12px', gridColumn: '1 / -1' };

const insightsRowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' };
const insightCardStyle = { padding: '22px 24px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '4px' };
const insightHeadStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--amber)', fontWeight: 500, marginBottom: '16px' };
const driverRowStyle = { display: 'grid', gridTemplateColumns: '1.4fr 70px 1fr 68px', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--line)', alignItems: 'center', fontSize: '12.5px' };
const driverNameStyle = { fontWeight: 500, color: 'var(--ink)' };
const driverBarStyle = { height: '6px', background: 'var(--line-dim)', borderRadius: '3px', overflow: 'hidden' };
const driverBarFillStyle = { display: 'block', height: '100%', background: 'var(--accent)' };
const driverLabelStyle = { textAlign: 'right', color: 'var(--muted)', fontSize: '11.5px' };
const driverLabelPrimaryStyle = { ...driverLabelStyle, color: 'var(--accent)', fontWeight: 600 };

const methodologyLinkStyle = { marginTop: '8px', textAlign: 'center' };
const methodologyBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
};
